import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { DeviceMobile, Desktop, AppleLogo, GoogleChromeLogo, WhatsappLogo, Envelope, Copy, X } from "@phosphor-icons/react";
import { QRCodeCanvas } from "qrcode.react";

const STORAGE_KEY = "install_prompt_shown_v1";

export default function InstallAppModal({ forceOpen = false, onOpenChange = null }) {
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState("android");
  const [isInstalled, setIsInstalled] = useState(false);
  const appUrl = window.location.origin;

  useEffect(() => {
    // Detect platform
    const ua = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) setPlatform("ios");
    else if (/android/.test(ua)) setPlatform("android");
    else setPlatform("desktop");
    // Detect if standalone (already installed)
    const standalone = window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone;
    setIsInstalled(!!standalone);
    // Auto-open once per user after successful auth
    if (!forceOpen) {
      const shown = localStorage.getItem(STORAGE_KEY);
      if (!shown && !standalone) {
        const t = setTimeout(() => {
          // Don't hijack the UI if user is already interacting with another dialog
          if (document.querySelector('[role="dialog"][data-state="open"]')) {
            localStorage.setItem(STORAGE_KEY, "1");
            return;
          }
          setOpen(true);
          localStorage.setItem(STORAGE_KEY, "1");
        }, 3000);
        return () => clearTimeout(t);
      }
    } else {
      setOpen(true);
    }
  }, [forceOpen]);

  const close = () => { setOpen(false); onOpenChange?.(false); };
  const shareWA = () => window.open(`https://wa.me/?text=${encodeURIComponent(`Baixe o Acerto Rápido JVO: ${appUrl}`)}`, "_blank");
  const shareEmail = () => window.open(`mailto:?subject=${encodeURIComponent("Acerto Rápido JVO")}&body=${encodeURIComponent(`Instale o app abrindo este link: ${appUrl}`)}`, "_blank");
  const copyUrl = async () => { try { await navigator.clipboard.writeText(appUrl); toast.success("Link copiado!"); } catch { toast.error("Erro ao copiar"); } };
  const downloadQR = () => {
    try {
      const canvas = document.querySelector('[data-testid="install-qr-code"] canvas');
      if (!canvas) { toast.error("QR Code não encontrado"); return; }
      const link = document.createElement("a");
      link.download = "acerto-rapido-jvo-qrcode.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("QR Code baixado!");
    } catch { toast.error("Falha ao baixar QR Code"); }
  };

  if (isInstalled) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); onOpenChange?.(o); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-extrabold flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            📱 Instale o app no seu dispositivo
          </DialogTitle>
          <DialogDescription>Aponte a câmera do celular no QR Code abaixo ou siga o passo-a-passo</DialogDescription>
        </DialogHeader>

        {/* QR Code destaque */}
        <div className="flex flex-col sm:flex-row gap-4 items-center bg-gradient-to-br from-blue-50 to-emerald-50 border-2 border-blue-200 rounded-xl p-4">
          <div className="bg-white p-3 rounded-lg shadow-md" data-testid="install-qr-code">
            <QRCodeCanvas value={appUrl} size={160} level="M" bgColor="#ffffff" fgColor="#1e40af" includeMargin={false} />
          </div>
          <div className="flex-1 space-y-2 text-center sm:text-left">
            <div className="font-bold text-blue-900 text-lg leading-tight">📷 Escaneie com a câmera</div>
            <ol className="text-sm text-slate-700 space-y-1 list-decimal list-inside">
              <li>Abra a <strong>câmera</strong> do seu celular</li>
              <li>Aponte para o QR Code</li>
              <li>Toque no link que aparece</li>
              <li>Siga as instruções abaixo pra instalar</li>
            </ol>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Button variant={platform === "android" ? "default" : "outline"} onClick={() => setPlatform("android")}
            data-testid="btn-tab-android" className={platform === "android" ? "bg-blue-600 hover:bg-blue-700 hover:text-white text-white gap-1.5" : "gap-1.5"}>
            <GoogleChromeLogo size={16} weight="duotone" /> Android
          </Button>
          <Button variant={platform === "ios" ? "default" : "outline"} onClick={() => setPlatform("ios")}
            data-testid="btn-tab-ios" className={platform === "ios" ? "bg-blue-600 hover:bg-blue-700 hover:text-white text-white gap-1.5" : "gap-1.5"}>
            <AppleLogo size={16} weight="duotone" /> iPhone
          </Button>
          <Button variant={platform === "desktop" ? "default" : "outline"} onClick={() => setPlatform("desktop")}
            data-testid="btn-tab-desktop" className={platform === "desktop" ? "bg-blue-600 hover:bg-blue-700 hover:text-white text-white gap-1.5" : "gap-1.5"}>
            <Desktop size={16} weight="duotone" /> PC
          </Button>
        </div>

        <Card className="p-4 bg-blue-50 border-blue-200">
          {platform === "android" && (
            <ol className="space-y-2 text-sm text-slate-800 list-decimal list-inside">
              <li>Abra este link no <strong>Chrome</strong> do seu Android</li>
              <li>Toque no ícone de <strong>3 pontinhos</strong> (menu) no canto superior direito</li>
              <li>Selecione <strong>"Instalar app"</strong> ou <strong>"Adicionar à tela inicial"</strong></li>
              <li>Confirme — vai virar ícone na sua tela como um app comum 🚛</li>
            </ol>
          )}
          {platform === "ios" && (
            <ol className="space-y-2 text-sm text-slate-800 list-decimal list-inside">
              <li>Abra este link no <strong>Safari</strong> do seu iPhone (não funciona no Chrome)</li>
              <li>Toque no botão <strong>Compartilhar</strong> ⬆️ (quadrado com seta pra cima)</li>
              <li>Role para baixo e toque em <strong>"Adicionar à Tela de Início"</strong></li>
              <li>Confirme — vai virar ícone na sua tela como um app comum 🚛</li>
            </ol>
          )}
          {platform === "desktop" && (
            <ol className="space-y-2 text-sm text-slate-800 list-decimal list-inside">
              <li>Abra este link no <strong>Google Chrome</strong> ou <strong>Microsoft Edge</strong></li>
              <li>Clique no <strong>ícone de instalar</strong> ⊕ que aparece na barra de endereço à direita</li>
              <li>Confirme — o app abrirá em janela separada, sem barra do navegador</li>
              <li>Também aparece atalho no menu Iniciar ou área de trabalho</li>
            </ol>
          )}
        </Card>

        <div className="border-2 border-dashed border-blue-300 rounded-lg p-3 flex items-center gap-2 bg-slate-50">
          <div className="flex-1 text-xs text-slate-600 truncate font-mono" data-testid="install-app-url">{appUrl}</div>
          <Button size="sm" variant="outline" onClick={copyUrl} data-testid="btn-copy-url" className="gap-1"><Copy size={14} /> Copiar</Button>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={shareWA} data-testid="btn-share-wa" className="bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white gap-1.5 flex-1"><WhatsappLogo size={16} weight="fill" /> Enviar por WhatsApp</Button>
          <Button onClick={shareEmail} variant="outline" data-testid="btn-share-email" className="border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-800 gap-1.5 flex-1"><Envelope size={16} /> E-mail</Button>
          <Button onClick={downloadQR} variant="outline" data-testid="btn-download-qr" className="border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-800 gap-1.5 flex-1">📥 Baixar QR</Button>
        </div>

        <p className="text-xs text-center text-muted-foreground pt-2">O app funciona <strong>offline</strong> e recebe atualizações automáticas sem precisar baixar de novo.</p>
      </DialogContent>
    </Dialog>
  );
}
